import { Autocomplete } from "@react-google-maps/api";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { IoMdSearch } from "react-icons/io";
import { PiMapPinLineFill } from "react-icons/pi";
import {
    getCleanedAddressFromAutoComplete,
    getLatLongGeocoder,
} from "../../../helpers/helpers";
import P from "../../../shared/typography/P";
import Button from "../../../shared/ui/Button";
import FormikLabeledInput from "../../../shared/ui/Form/FormikLabeledInput";
import { ProjectFormSchema } from "../../../shared/ui/Form/schema";
import ReusableMap from "../../../shared/ui/Map";

const center = {
    lat: 18.5204303,
    lng: 73.8567437,
};
const ProjectForm = ({ initialValues, onSubmit }) => {
    const [useManualAddress, setUseManualAddress] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const locationData = {
        lat: initialValues?.latitude,
        lng: initialValues?.longitude,
        address: initialValues?.address,
        city: initialValues?.city,
        state: initialValues?.state,
        pincode: initialValues?.pincode,
    };
    const [autoFillAddress, setAutoFillAddress] = useState(
        locationData || "Pune, Maharashtra, India"
    );
    const [markerPointA, setMarkerPointA] = useState(center);
    const autocompleteRef = useRef(null);
    const siteRef = useRef();

    const handlePlaceChanged = async (setFieldValue, setFieldTouched) => {
        const place = autocompleteRef.current.getPlace();
        const suggestion = siteRef.current.value;

        if (!place || !place.geometry) {
            console.error("No details available for input: ");
            return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const { city, state, cleanedAddress, pincode } =
            await getCleanedAddressFromAutoComplete(
                place,
                suggestion,
                lat,
                lng
            );

        const locationData = {
            lat,
            lng,
            address: cleanedAddress,
            city,
            state,
            pincode,
        };

        setAutoFillAddress(locationData);
        setMarkerPointA({ lat, lng });

        setFieldValue("address", cleanedAddress);
        setFieldValue("city", city);
        setFieldValue("state", state);
        setFieldValue("pincode", pincode);
    };

    useEffect(() => {
        if (initialValues?.address) {
            (async () => {
                const res = await getLatLongGeocoder(initialValues.address);
                setAutoFillAddress(initialValues.address);
                setMarkerPointA(res);
                setUseManualAddress(false);
                setIsLoaded(true);
                if (initialValues?.latitude && initialValues?.longitude) {
                    setMarkerPointA({
                        lat: Number(initialValues?.latitude),
                        lng: Number(initialValues?.longitude),
                    });
                }
            })();
        } else {
            setIsLoaded(true);
        }
    }, [initialValues?.address]);
    return (
        <Formik
            initialValues={{
                projectName: initialValues?.projectName || "",
                address: initialValues?.address || "",
                city: initialValues?.city || "",
                state: initialValues?.state || "",
                pincode: initialValues?.pincode || "",
                latitude: initialValues?.latitude,
                longitude: initialValues?.longitude,
            }}
            validationSchema={ProjectFormSchema}
            onSubmit={(values, { setTouched, validateForm }) => {
                if (!useManualAddress) {
                    setUseManualAddress((prev) => !prev);

                    setTouched({
                        address: true,
                        city: true,
                        state: true,
                        pincode: true,
                    });
                    validateForm();
                } else {
                    onSubmit(values);
                }
            }}
        >
            {({
                values,
                errors,
                setFieldValue,
                setFieldTouched,
                isValid,
                isSubmitting,
                dirty,
            }) => (
                <Form className="space-y-2 p-3">
                    <div className="grid grid-cols-1 mt-1 gap-2">
                        {!useManualAddress ? (
                            isLoaded ? (
                                <>
                                    <FormikLabeledInput
                                        label="Project Name"
                                        name="projectName"
                                        placeholder="Enter project name"
                                        required
                                    />
                                    <Autocomplete
                                        onPlaceChanged={() =>
                                            handlePlaceChanged(
                                                setFieldValue,
                                                setFieldTouched
                                            )
                                        }
                                        onLoad={(autocomplete) => {
                                            autocompleteRef.current =
                                                autocomplete;
                                        }}
                                    >
                                        <FormikLabeledInput
                                            placeholder="Enter Your Location"
                                            label="Search Address"
                                            required
                                            ref={siteRef}
                                            name="address"
                                            id="siteLocationId"
                                            shadow={false}
                                            icon={<IoMdSearch />}
                                            iconWrapperClass="text-primary"
                                        />
                                    </Autocomplete>
                                </>
                            ) : (
                                <P>Loading...</P>
                            )
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] xl:gap-x-3.2 xl:mt-3.6">
                                    <ReusableMap
                                        center={markerPointA}
                                        dimensions="w-[100%] h-[200px]"
                                        address={autoFillAddress}
                                        callback={() => setIsLoaded(true)}
                                    />
                                </div>
                                <p className=" text-xs">
                                    Update the address if not showing correctly
                                </p>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3 mt-2">
                                    <FormikLabeledInput
                                        label="Address"
                                        name="address"
                                        placeholder="Address"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="City"
                                        name="city"
                                        placeholder="City"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="State"
                                        name="state"
                                        placeholder="State"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="Pincode"
                                        name="pincode"
                                        placeholder="Pincode"
                                        required
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {!useManualAddress && (
                        <>
                            <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] xl:gap-x-3.2 xl:mt-3.6">
                                <ReusableMap
                                    center={markerPointA}
                                    dimensions="w-[100%] h-[200px]"
                                    address={autoFillAddress}
                                    callback={() => setIsLoaded(true)}
                                />
                            </div>
                            <>
                                {values.address &&
                                    values.projectName &&
                                    isValid && (
                                        <div className="mt-2">
                                            <div className="flex items-center gap-1">
                                                <PiMapPinLineFill
                                                    size={26}
                                                    className="text-orange"
                                                />
                                                <P className="text-sm text-orange font-vietnam">
                                                    {values.projectName}
                                                </P>
                                            </div>

                                            <P className="text-xs font-vietnam">
                                                {values.address}
                                            </P>
                                        </div>
                                    )}
                            </>
                        </>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={!isValid}
                    >
                        {useManualAddress
                            ? "Submit Details"
                            : "Confirm Location"}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default ProjectForm;
